import React from "react";
import StandalonePageLayout from "../layout/StandalonePageLayout";
import {
    usePrintReady,
    DocOpening,
    DocSection,
    SubHead,
    P,
    T,
    Steps,
    List,
    Note,
    MarginNote,
    Code,
    InlineCode,
    MailLink,
    DocTable,
    Colophon,
} from "./DocParts";

function PartnerGuide() {
    usePrintReady();

    return (
        <StandalonePageLayout>
            <DocOpening
                appendix="C"
                label="Partner API"
                title="Partner API Documentation"
                intro="Integration guide for JPRO partners"
            />

            <DocSection number="C.1" label="Onboarding" title="Partner Onboarding">
                <P>
                    JPRO offers white-label integration for organizations that want to embed our AI capabilities
                    into their platforms.
                </P>

                <SubHead>Becoming a Partner</SubHead>
                <Steps
                    items={[
                        <>Contact our partnership team at <MailLink /></>,
                        "Complete the partnership application and agreement",
                        "Receive your unique partner account and credentials",
                        "Get access to your partner dashboard",
                    ]}
                />

                <SubHead>What You Get</SubHead>
                <List
                    items={[
                        "Dedicated partner dashboard for managing integrations",
                        "Unique API keys for secure access",
                        "Embeddable widgets for seamless integration",
                        "Usage analytics and reporting",
                        "Priority technical support",
                    ]}
                />
            </DocSection>

            <DocSection
                number="C.2"
                label="API keys"
                title="Getting Your API Key"
                aside={<MarginNote>Treat your API key like a password.</MarginNote>}
            >
                <SubHead>Accessing Your Keys</SubHead>
                <Steps
                    items={[
                        "Log in to your JPRO partner account",
                        <>Navigate to <T>Settings → Partner Integration</T></>,
                        <>Click <T>&quot;Generate New API Key&quot;</T></>,
                        "Copy and securely store your API key",
                    ]}
                />

                <Note label="Security note">
                    Treat your API key like a password. Never expose it in client-side code or public repositories.
                    Store it securely in environment variables.
                </Note>

                <SubHead>Managing Keys</SubHead>
                <List
                    items={[
                        "You can have multiple active API keys",
                        "Keys can be revoked instantly from your dashboard",
                        "Rotate keys regularly for security best practices",
                        "Each key tracks its own usage metrics",
                    ]}
                />
            </DocSection>

            <DocSection number="C.3" label="Embedding" title="Embedding JPRO">
                <SubHead>Basic Embed</SubHead>
                <P>The simplest way to integrate JPRO is through an iframe embed:</P>
                <Code>{`<iframe
  src="https://jpro.app/embed?key=YOUR_API_KEY"
  width="100%"
  height="600"
  frameborder="0"
  allow="microphone; clipboard-write"
></iframe>`}</Code>

                <SubHead>Customization Options</SubHead>
                <P>Add URL parameters to customize the embed:</P>
                <List
                    items={[
                        <><InlineCode>theme=dark</InlineCode> - Force dark or light theme</>,
                        <><InlineCode>feature=chat</InlineCode> - Open specific feature (chat, studio, analyzer)</>,
                        <><InlineCode>compact=true</InlineCode> - Use compact UI mode</>,
                    ]}
                />

                <SubHead>Example: Custom Embed</SubHead>
                <Code>{`<iframe
  src="https://jpro.app/embed?key=YOUR_API_KEY&theme=dark&feature=analyzer&compact=true"
  width="100%"
  height="700"
  frameborder="0"
></iframe>`}</Code>

                <SubHead>Responsive Design</SubHead>
                <P>For responsive embedding, use CSS:</P>
                <Code>{`.jpro-embed {
  position: relative;
  width: 100%;
  padding-bottom: 75%; /* 4:3 aspect ratio */
}

.jpro-embed iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}`}</Code>
            </DocSection>

            <DocSection number="C.4" label="Managing" title="Managing Your Integration">
                <SubHead>Partner Dashboard</SubHead>
                <P>
                    Access your partner dashboard at <T>Settings → Partner Integration</T>:
                </P>
                <List
                    items={[
                        <><T>Usage Analytics:</T> Track API calls, user sessions, and feature usage</>,
                        <><T>Active Integrations:</T> Monitor all embedded instances</>,
                        <><T>API Key Management:</T> Create, rotate, and revoke keys</>,
                        <><T>Billing Overview:</T> View current usage against limits</>,
                    ]}
                />

                <SubHead>Monitoring Usage</SubHead>
                <P>Real-time metrics available in your dashboard:</P>
                <List
                    items={[
                        "Total API requests (daily, weekly, monthly)",
                        "Active user sessions",
                        "Feature usage breakdown (Chat, Studio, Analyzer)",
                        "Response times and error rates",
                        "Storage usage for uploaded documents",
                    ]}
                />
            </DocSection>

            <DocSection
                number="C.5"
                label="Limits"
                title="Usage & Rate Limits"
                aside={<MarginNote>100 requests per minute, per API key.</MarginNote>}
            >
                <SubHead>Standard Limits</SubHead>
                <DocTable
                    head={["Resource", "Limit", "Notes"]}
                    rows={[
                        ["API Requests", "10,000/day", "Contact us for higher limits"],
                        ["File Uploads", "50MB per file", "PDF, DOCX, XLSX supported"],
                        ["Image Generation", "100/day", "Applies to Studio feature"],
                        ["Storage", "10GB total", "Across all documents"],
                    ]}
                />

                <SubHead>Rate Limiting</SubHead>
                <List
                    items={[
                        "Requests are limited to 100 per minute per API key",
                        "Exceeding limits returns HTTP 429 (Too Many Requests)",
                        "Implement exponential backoff in your integration",
                        <>Monitor the <InlineCode>X-RateLimit-Remaining</InlineCode> header</>,
                    ]}
                />

                <SubHead>Upgrading Limits</SubHead>
                <P>
                    Need higher limits? Contact <MailLink /> with:
                </P>
                <List
                    items={[
                        "Your current usage patterns",
                        "Expected growth in the next 3-6 months",
                        "Specific features you'll be using most",
                    ]}
                />
            </DocSection>

            <DocSection number="C.6" label="Support" title="Support & Resources">
                <SubHead>Technical Support</SubHead>
                <P>Partners receive priority support:</P>
                <List
                    items={[
                        <><T>Email:</T> <MailLink /></>,
                        <><T>Response Time:</T> Within 4 business hours for partners</>,
                        <><T>Include:</T> Your API key prefix, error messages, and integration details</>,
                    ]}
                />

                <SubHead>Best Practices</SubHead>
                <List
                    items={[
                        "Test integrations in a staging environment first",
                        "Implement error handling for API rate limits",
                        "Cache responses when appropriate to reduce API calls",
                        "Monitor your dashboard regularly for unusual patterns",
                        "Keep API keys secure and rotate them periodically",
                    ]}
                />

                <SubHead>Common Issues</SubHead>
                <List
                    items={[
                        <><T>Embed not loading:</T> Verify API key is active and correctly formatted in URL</>,
                        <><T>Rate limit errors:</T> Implement request throttling and exponential backoff</>,
                        <><T>CORS issues:</T> Ensure your domain is whitelisted in partner settings</>,
                        <><T>Upload failures:</T> Check file size (50MB max) and format (PDF/DOCX/XLSX)</>,
                    ]}
                />
            </DocSection>

            <Colophon>© 2025 Zeffert & Associates. All rights reserved.</Colophon>
        </StandalonePageLayout>
    );
}

export default PartnerGuide;
